import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { api } from '../App';
import Button from '@shared/ui/Button';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage: React.FC = () => {
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const forgotPasswordMutation = useMutation(
    async (data: ForgotPasswordFormData) => {
      const response = await api.post('/auth/forgot-password', data);
      return response.data;
    },
  );

  const onSubmit = (values: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(values);
  };

  const isSubmitting = forgotPasswordMutation.isLoading || form.formState.isSubmitting;

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark-secondary flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full card">
        <div className="p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-5 w-5 text-primary-700 dark:text-primary-400" />
            <h1 className="text-xl font-bold text-text-primary dark:text-text-dark-primary">
              Forgot your password?
            </h1>
          </div>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Enter the email address associated with your account and we&apos;ll send you a link to reset your password.
          </p>

          {forgotPasswordMutation.isSuccess && (
            <div className="rounded-md border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary px-3 py-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              If an account exists for that email, a reset link has been sent. Please check your inbox.
            </div>
          )}

          {forgotPasswordMutation.isError && !forgotPasswordMutation.isSuccess && (
            <div className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700">
              Something went wrong. Please try again in a moment.
            </div>
          )}

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                {...form.register('email')}
                aria-invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email && (
                <p className="mt-1 text-xs text-error-600">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="primary"
              className="w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending link…' : 'Send reset link'}
            </Button>
          </form>

          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary text-center">
            Remembered your password?{' '}
            <a href="/login" className="text-primary-700 dark:text-primary-400 hover:underline">
              Back to login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

